import React, { Component, Fragment } from "react";
import { inject, observer } from "mobx-react";
import moment from "moment";
import currencyHelper from "../../helper/currency";

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Checkbox,
    Chip,
    CircularProgress,
    ExpansionPanel,
    ExpansionPanelSummary,
    ExpansionPanelDetails
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import agent from "../../agent";
import { SuperLedgerStatus } from "../../types/enum";
import SuperPayStatusPanel from "../../components/superPay/SuperPayStatusPanel";

@inject("profileStore", "superLedgerPaymentStore", "templateStore")
@observer
class PaymentDetailModal extends Component {
    constructor(props) {
        super(props);
        this.state = {
            selectedLedgerIds: new Set(),
            expandedPayees: new Set(),
            isRemoving: false,
            showConfirmDialog: false,
            isSyncing: false,
            isMarkingPaid: false
        };
    }

    componentDidUpdate(prevProps) {
        const { superLedgerPaymentStore } = this.props;
        const prevLedgers = prevProps.superLedgerPaymentStore?.selectedPaymentLedgers || [];
        const currentLedgers = superLedgerPaymentStore?.selectedPaymentLedgers || [];
        
        if (this.props.payment && this.props.payment !== prevProps.payment) {
            this.setState({ selectedLedgerIds: new Set(), expandedPayees: new Set() });
            const profileId = this.props.profileStore.currentProfile?.id;
            superLedgerPaymentStore.getPaymentById(this.props.payment.id, profileId);
        }
        
        if (this.props.open && !prevProps.open) {
            this.setState({ selectedLedgerIds: new Set(), expandedPayees: new Set() });
        }
        
        if (currentLedgers.length > 0 && prevLedgers.length === 0 && this.state.expandedPayees.size === 0) {
            const grouped = this.getGroupedByPayee();
            const expandedPayees = new Set(Object.keys(grouped));
            this.setState({ expandedPayees });
        }
    }

    handleClose = () => {
        this.setState({ selectedLedgerIds: new Set(), expandedPayees: new Set() });
        this.props.superLedgerPaymentStore.clearSelectedPayment();
        this.props.onClose();
    };

    handleMarkAsPaid = async () => {
        const { payment } = this.props;
        const { selectedPayment } = this.props.superLedgerPaymentStore;
        const paymentData = selectedPayment || payment;
        
        if (!paymentData?.id) return;
        
        this.setState({ isMarkingPaid: true });
        
        try {
            const response = await agent.SuperLedgerPayments.markPaymentAsSettled(paymentData.id);
            
            if (response.status === 401) {
                this.props.templateStore.openSnackbar("You don't have permission for this action");
                return;
            }
            if (response.status === 400) {
                this.props.templateStore.openSnackbar(response.message || "Failed to mark payment as paid");
                return;
            }
            if (response.isError) {
                this.props.templateStore.openSnackbar(response.message || "Failed to mark payment as paid");
                return;
            }
            
            this.props.templateStore.openSnackbar("Payment marked as paid successfully");
            const profileId = this.props.profileStore.currentProfile?.id;
            this.props.superLedgerPaymentStore.getPaymentById(paymentData.id, profileId);
            if (this.props.onPaymentUpdated) {
                this.props.onPaymentUpdated();
            }
        } catch (error) {
            if (error?.response?.status === 401) {
                this.props.templateStore.openSnackbar("You don't have permission for this action");
            } else if (error?.response?.status === 400) {
                this.props.templateStore.openSnackbar(error?.response?.data?.message || "Failed to mark payment as paid");
            } else {
                this.props.templateStore.openSnackbar("Error marking payment as paid: " + (error.message || "Unknown error"));
            }
        } finally {
            this.setState({ isMarkingPaid: false });
        }
    };

    handleSyncPaymentStatus = async () => {
        const { payment } = this.props;
        const { selectedPayment } = this.props.superLedgerPaymentStore;
        const paymentData = selectedPayment || payment;
        
        if (!paymentData?.id) return;
        
        this.setState({ isSyncing: true });

        const isHgSuperPay = paymentData.sourceType?.toLowerCase() === "mygigsters";
        
        try {
            if (isHgSuperPay) {
                await agent.HgSuperPay.syncPaymentStatus(paymentData.id);
                this.props.templateStore.openSnackbar("Status synced successfully");
                const profileId = this.props.profileStore.currentProfile?.id;
                this.props.superLedgerPaymentStore.getPaymentById(paymentData.id, profileId);
                if (this.props.onPaymentUpdated) {
                    this.props.onPaymentUpdated();
                }
            } else {
                const response = await agent.Xero.syncPayment(paymentData.id);
                if (response.success) {
                    if (response.statusChanged) {
                        this.props.templateStore.openSnackbar(`Payment status updated: ${response.previousStatus} → ${response.newStatus}`);
                    } else if (response.message) {
                        this.props.templateStore.openSnackbar(response.message);
                    } else {
                        this.props.templateStore.openSnackbar(`Payment status unchanged - still ${response.currentStatus}`);
                    }
                    const profileId = this.props.profileStore.currentProfile?.id;
                    this.props.superLedgerPaymentStore.getPaymentById(paymentData.id, profileId);
                    if (this.props.onPaymentUpdated) {
                        this.props.onPaymentUpdated();
                    }
                } else {
                    this.props.templateStore.openSnackbar(response.error || "Failed to sync payment status");
                }
            }
        } catch (error) {
            this.props.templateStore.openSnackbar("Failed to sync status");
        } finally {
            this.setState({ isSyncing: false });
        }
    };

    getFilteredLedgers = () => {
        const { selectedPaymentLedgers } = this.props.superLedgerPaymentStore;
        const { viewMode } = this.props;
        
        if (viewMode === 'payee') {
            const currentProfile = this.props.profileStore.getCurrentProfile();
            if (currentProfile?.id) {
                return selectedPaymentLedgers.filter(ledger => ledger.payeeId === currentProfile.id);
            }
        }
        
        return selectedPaymentLedgers;
    };

    getGroupedByPayee = () => {
        const { selectedPayment } = this.props.superLedgerPaymentStore;
        const filteredLedgers = this.getFilteredLedgers();
        const payees = selectedPayment?.payees || [];
        
        const grouped = {};
        
        filteredLedgers.forEach(ledger => {
            const payeeId = ledger.payeeId || "unknown";
            if (!grouped[payeeId]) {
                const payeeInfo = payees.find(p => p.payeeId === payeeId);
                grouped[payeeId] = {
                    payeeId,
                    payeeName: this.getPayeeNameFromInfo(payeeInfo),
                    items: []
                };
            }
            grouped[payeeId].items.push(ledger);
        });
        
        return grouped;
    };

    getPayeeNameFromInfo = (payeeInfo) => {
        if (!payeeInfo) return "Unknown";
        if (payeeInfo.profileName) return payeeInfo.profileName;
        const fullName = `${payeeInfo.firstName || ''} ${payeeInfo.lastName || ''}`.trim();
        return fullName || "Unknown";
    };

    handlePayeeToggle = (payeeId) => {
        const { selectedLedgerIds } = this.state;
        const grouped = this.getGroupedByPayee();
        const payeeGroup = grouped[payeeId];
        
        if (!payeeGroup) return;
        
        const newSelectedIds = new Set(selectedLedgerIds);
        const payeeItemIds = payeeGroup.items.map(item => item.id);
        const allSelected = payeeItemIds.every(id => newSelectedIds.has(id));
        
        if (allSelected) {
            payeeItemIds.forEach(id => newSelectedIds.delete(id));
        } else {
            payeeItemIds.forEach(id => newSelectedIds.add(id));
        }
        
        this.setState({ selectedLedgerIds: newSelectedIds });
    };

    handleItemToggle = (ledgerId) => {
        const { selectedLedgerIds } = this.state;
        const newSelectedIds = new Set(selectedLedgerIds);
        
        if (newSelectedIds.has(ledgerId)) {
            newSelectedIds.delete(ledgerId);
        } else {
            newSelectedIds.add(ledgerId);
        }
        
        this.setState({ selectedLedgerIds: newSelectedIds });
    };

    handleExpandToggle = (payeeId) => {
        const { expandedPayees } = this.state;
        const newExpanded = new Set(expandedPayees);
        
        if (newExpanded.has(payeeId)) {
            newExpanded.delete(payeeId);
        } else {
            newExpanded.add(payeeId);
        }
        
        this.setState({ expandedPayees: newExpanded });
    };

    handleSelectAll = () => {
        const filteredLedgers = this.getFilteredLedgers();
        const { selectedLedgerIds } = this.state;
        
        if (selectedLedgerIds.size === filteredLedgers.length) {
            this.setState({ selectedLedgerIds: new Set() });
        } else {
            this.setState({
                selectedLedgerIds: new Set(filteredLedgers.map(l => l.id))
            });
        }
    };

    handleRemoveFromPayment = () => {
        const { payment } = this.props;
        const { selectedLedgerIds } = this.state;
        
        if (!payment || selectedLedgerIds.size === 0) return;
        
        this.setState({ showConfirmDialog: true });
    };

    handleCloseConfirmDialog = () => {
        this.setState({ showConfirmDialog: false });
    };

    handleConfirmRemove = () => {
        const { payment, onDeleteSuccess } = this.props;
        const filteredLedgers = this.getFilteredLedgers();
        const { selectedLedgerIds } = this.state;
        
        if (!payment || selectedLedgerIds.size === 0) return;
        
        // Get the payment ledger IDs (junction table IDs) for removal
        const selectedLedgers = filteredLedgers.filter(ledger => selectedLedgerIds.has(ledger.id));
        const paymentLedgerIds = selectedLedgers.map(ledger => ledger.id).filter(id => id != null);
        
        // Validate all selected items have valid IDs
        if (paymentLedgerIds.length !== selectedLedgers.length) {
            this.props.templateStore.openSnackbar("Some items are missing the required identifier and cannot be removed");
            return;
        }
        
        this.setState({ isRemoving: true, showConfirmDialog: false });
        
        this.props.superLedgerPaymentStore.removeLedgersFromPayment(payment.id, paymentLedgerIds)
            .then((result) => {
                if (result?.success) {
                    let message = `${result.removedCount} item${result.removedCount !== 1 ? 's' : ''} removed from payment. New total: ${this.getCurrencySymbol(this.props.superLedgerPaymentStore.selectedPayment?.currency)}${currencyHelper.formatMoney(result.newTotalAmount, 2)}`;
                    
                    // Check if any Xero sync failed
                    const xeroSyncResults = result.xeroSyncResults || [];
                    const hasXeroSyncFailure = xeroSyncResults.some(sync => sync.success === false);
                    if (hasXeroSyncFailure) {
                        message += ". Warning: Failed to update Xero - please update manually.";
                    }
                    
                    this.props.templateStore.openSnackbar(message);
                    this.setState({ selectedLedgerIds: new Set() });
                    if (onDeleteSuccess) {
                        onDeleteSuccess();
                    }
                } else {
                    this.props.templateStore.openSnackbar("Failed to remove items from payment");
                }
            })
            .catch(error => {
                this.props.templateStore.openSnackbar("Failed to remove items from payment");
                console.error("Remove from payment error:", error);
            })
            .finally(() => {
                this.setState({ isRemoving: false });
            });
    };

    getSelectedLedgerItems = () => {
        const { selectedPayment } = this.props.superLedgerPaymentStore;
        const filteredLedgers = this.getFilteredLedgers();
        const { selectedLedgerIds } = this.state;
        const payees = selectedPayment?.payees || [];
        
        return filteredLedgers.filter(ledger => selectedLedgerIds.has(ledger.id)).map(ledger => {
            const payeeInfo = payees.find(p => p.payeeId === ledger.payeeId);
            return {
                ...ledger,
                payeeName: this.getPayeeNameFromInfo(payeeInfo)
            };
        });
    };

    getCurrencySymbol = (currency) => {
        if (!currency) {
            const { currentProfile } = this.props.profileStore;
            currency = currentProfile?.currency || "AUD";
        }
        
        switch (currency) {
            case "GBP": return "£";
            case "EUR": return "€";
            case "USD":
            case "AUD":
            case "NZD":
            default: return "$";
        }
    };

    getStatusChip = (status) => {
        const statusColors = {
            pending: { background: "#FFF3E0", color: "#E65100" },
            submitted: { background: "#E3F2FD", color: "#1565C0" },
            settled: { background: "#E8F5E9", color: "#2E7D32" },
            paid: { background: "#E8F5E9", color: "#2E7D32" },
            failed: { background: "#FFEBEE", color: "#C62828" },
            partiallypaid: { background: "#FFF8E1", color: "#F57F17" },
            settled_with_warning: { background: "#FFF8E1", color: "#F57F17" },
            paidinfunds: { background: "#FFF8E1", color: "#F57F17" },
            refund: { background: "#FCE4EC", color: "#880E4F" },
            refunded: { background: "#FCE4EC", color: "#880E4F" },
            processing: { background: "#E3F2FD", color: "#1565C0" },
            cancelled: { background: "#EEEEEE", color: "#616161" }
        };
        
        const s = (status || "").toLowerCase().replace(/\s+/g, "");
        const style = statusColors[s] || { background: "#EEEEEE", color: "#616161" };
        const displayLabel = s === "partiallypaid" ? "Partially Paid"
            : s === "settled_with_warning" ? "Settled with Warning"
            : s === "paidinfunds" ? "Paid in Funds"
            : (status || "Unknown");
        
        return (
            <Chip 
                label={displayLabel} 
                style={{ 
                    backgroundColor: style.background, 
                    color: style.color,
                    fontWeight: 500,
                    height: 24,
                    fontSize: 12
                }} 
            />
        );
    };

    getPayeePaymentDetail = (payeeId) => {
        const paymentData = this.props.superLedgerPaymentStore.selectedPayment || this.props.payment || {};
        const details = paymentData.paymentDetails || [];
        return details.find(d => String(d.payeeId) === String(payeeId)) || null;
    };

    getDerivedStatus = () => {
        const { selectedPayment } = this.props.superLedgerPaymentStore;
        const payment = this.props.payment || {};
        const paymentData = selectedPayment || payment;
        const details = paymentData.paymentDetails || [];

        if (!details.length) return paymentData.status;

        const FINAL_STATES = new Set(["paid", "failed", "refund"]);
        const statuses = details.map(d => (d.status || "").toLowerCase());

        const allFinal = statuses.every(s => FINAL_STATES.has(s));
        if (!allFinal) return "Paid in Funds";

        const allSame = statuses.every(s => s === statuses[0]);
        if (allSame) return details[0].status;

        const allFailed = statuses.every(s => s === "failed");
        if (allFailed) return "Failed";

        return "PartiallyPaid";
    };

    getLedgerStatusLabel = (status) => {
        const statusEnum = SuperLedgerStatus.enumValues[status];
        return statusEnum ? statusEnum.name : "Unknown";
    };

    getPayeeName = (payeeId, payees) => {
        if (!payees || !payeeId) return "-";
        const payee = payees.find(p => p.payeeId === payeeId);
        if (!payee) return "-";
        if (payee.profileName) return payee.profileName;
        const fullName = `${payee.firstName || ''} ${payee.lastName || ''}`.trim();
        return fullName || "-";
    };

    getPayerName = (payer) => {
        if (!payer) return "-";
        if (payer.profileName) return payer.profileName;
        const fullName = `${payer.firstName || ''} ${payer.lastName || ''}`.trim();
        return fullName || "-";
    };

    getPayeesDisplay = (payees) => {
        if (!payees || payees.length === 0) return "-";
        return payees.map(p => {
            if (p.profileName) return p.profileName;
            const fullName = `${p.firstName || ''} ${p.lastName || ''}`.trim();
            return fullName || "Unknown";
        }).join(", ");
    };

    renderBookingLink = (bookingId) => {
        if (!bookingId) return "-";
        
        const isMobile = process.env.REACT_APP_IS_MOBILE === "true";
        
        if (isMobile) {
            return bookingId;
        }
        
        return (
            <a 
                href={`/booking/${bookingId}/1`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#1976d2", textDecoration: "none" }}
            >
                View Booking
            </a>
        );
    };

    canModifyPayment = () => {
        const { payment, viewMode } = this.props;
        const { selectedPayment } = this.props.superLedgerPaymentStore;
        const { currentProfile } = this.props.profileStore;
        
        if (viewMode === 'payee') return false;
        if (!payment || !currentProfile) return false;
        
        const paymentData = selectedPayment || payment;
        const isSubmitted = paymentData.status?.toLowerCase() === 'submitted';
        const isCurrentUserPayer = paymentData.payerId === currentProfile.id;
        
        return isSubmitted && isCurrentUserPayer;
    };

    getPayeeSubtotal = (items, currencySymbol) => {
        const total = items.reduce((sum, item) => sum + parseFloat(item.allocatedAmount || 0), 0);
        return `${currencySymbol}${currencyHelper.formatMoney(total, 2)}`;
    };

    isPayeeFullySelected = (payeeId) => {
        const { selectedLedgerIds } = this.state;
        const grouped = this.getGroupedByPayee();
        const payeeGroup = grouped[payeeId];
        
        if (!payeeGroup || payeeGroup.items.length === 0) return false;
        
        return payeeGroup.items.every(item => selectedLedgerIds.has(item.id));
    };

    isPayeePartiallySelected = (payeeId) => {
        const { selectedLedgerIds } = this.state;
        const grouped = this.getGroupedByPayee();
        const payeeGroup = grouped[payeeId];
        
        if (!payeeGroup || payeeGroup.items.length === 0) return false;
        
        const selectedCount = payeeGroup.items.filter(item => selectedLedgerIds.has(item.id)).length;
        return selectedCount > 0 && selectedCount < payeeGroup.items.length;
    };

    renderConfirmDialog = () => {
        const { showConfirmDialog } = this.state;
        const { selectedPayment } = this.props.superLedgerPaymentStore;
        const currencySymbol = this.getCurrencySymbol(selectedPayment?.currency);
        const selectedItems = this.getSelectedLedgerItems();
        
        const totalAmount = selectedItems.reduce((sum, item) => sum + parseFloat(item.allocatedAmount || 0), 0);
        
        return (
            <Dialog
                open={showConfirmDialog}
                onClose={this.handleCloseConfirmDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Confirm Removal</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" style={{ marginBottom: 16 }}>
                        Are you sure you want to remove the following {selectedItems.length} item{selectedItems.length !== 1 ? 's' : ''} from this payment?
                    </Typography>
                    
                    <div style={{ maxHeight: 300, overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: 4 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow style={{ backgroundColor: '#f5f5f5' }}>
                                    <TableCell>Payee</TableCell>
                                    <TableCell>Amount</TableCell>
                                    <TableCell>Booking</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {selectedItems.map((item, index) => (
                                    <TableRow key={item.id || index}>
                                        <TableCell>{item.payeeName}</TableCell>
                                        <TableCell>{currencySymbol}{currencyHelper.formatMoney(parseFloat(item.allocatedAmount || 0), 2)}</TableCell>
                                        <TableCell>{item.bookingId || '-'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                    
                    <Typography variant="subtitle1" style={{ marginTop: 16, fontWeight: 500 }}>
                        Total to be removed: {currencySymbol}{currencyHelper.formatMoney(totalAmount, 2)}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={this.handleCloseConfirmDialog} color="default">
                        Cancel
                    </Button>
                    <Button 
                        onClick={this.handleConfirmRemove} 
                        color="secondary"
                        variant="contained"
                    >
                        Remove Items
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    render() {
        const { open, payment, viewMode } = this.props;
        const { isLoadingPayment, selectedPayment } = this.props.superLedgerPaymentStore;
        const { selectedLedgerIds, expandedPayees, isRemoving } = this.state;
        
        const filteredLedgers = this.getFilteredLedgers();
        const paymentData = selectedPayment || payment || {};
        const currencySymbol = this.getCurrencySymbol(paymentData.currency);
        const canModify = this.canModifyPayment();
        const isHgSuperPay = paymentData.sourceType?.toLowerCase() === "mygigsters";
        const allSelected = filteredLedgers.length > 0 && selectedLedgerIds.size === filteredLedgers.length;
        const filteredTotal = filteredLedgers.reduce((sum, item) => sum + parseFloat(item.allocatedAmount || 0), 0);
        const displayTotal = viewMode === 'payee' ? filteredTotal : parseFloat(selectedPayment?.totalAmount || 0);
        const hasSelectedItems = selectedLedgerIds.size > 0;
        const grouped = this.getGroupedByPayee();
        const payeeIds = Object.keys(grouped);

        return (
            <Fragment>
            {this.renderConfirmDialog()}
            <Dialog
                open={open}
                onClose={this.handleClose}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    Payment Details
                    {payment && (
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    marginTop: 4
                                }}
                            >
                                <Typography variant="body2" color="textSecondary">
                                    {paymentData.paymentDate ? moment(paymentData.paymentDate).format("DD MMM YYYY HH:mm") : "-"} -
                                </Typography>
                                {this.getStatusChip(isHgSuperPay ? this.getDerivedStatus() : (paymentData.status || payment.status))}
                            </div>
                    )}
                </DialogTitle>
                <DialogContent>
                    {isLoadingPayment ? (
                        <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
                            <CircularProgress />
                        </div>
                    ) : (
                        <Fragment>
                            {selectedPayment && (
                                <div style={{ marginBottom: 20, display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 24px" }}>
                                    <Typography variant="body1">
                                        <strong>Total Amount:</strong> {currencySymbol}{currencyHelper.formatMoney(displayTotal, 2)} {selectedPayment.currency || ""}
                                    </Typography>
                                    <Typography variant="body1">
                                        <strong>Payment Method:</strong> {selectedPayment.sourceType?.toUpperCase() === "MYGIGSTERS" ? "HG Super Pay" : (selectedPayment.paymentMethod || "-")}
                                    </Typography>

                                    <Typography variant="body1">
                                        <strong>Date Range:</strong> {selectedPayment.startDate ? moment(selectedPayment.startDate).format("DD MMM YYYY") : "-"} - {selectedPayment.endDate ? moment(selectedPayment.endDate).format("DD MMM YYYY") : "-"}
                                    </Typography>
                                    <Typography variant="body1">
                                        <strong>Payer:</strong> {this.getPayerName(selectedPayment.payer)}
                                    </Typography>
                                    {selectedPayment.notes && (
                                        <Typography variant="body1" style={{ gridColumn: "1 / -1" }}>
                                            <strong>Notes:</strong> {selectedPayment.notes}
                                        </Typography>
                                    )}
                                </div>
                            )}

                            {paymentData.mygigstersStatus?.toUpperCase() === "SETTLED_WITH_WARNING" && (
                                <div style={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: 10,
                                    background: "#FFFDE7",
                                    border: "1px solid #F9A825",
                                    borderRadius: 6,
                                    padding: "12px 16px",
                                    marginBottom: 16
                                }}>
                                    <span style={{ fontSize: 18, lineHeight: 1.3, color: "#F57F17" }}>⚠</span>
                                    <Typography variant="body2" style={{ color: "#795548", lineHeight: 1.6 }}>
                                        <strong style={{ color: "#F57F17" }}>Settled with Warnings — </strong>
                                        check each payee below for individual details.
                                    </Typography>
                                </div>
                            )}

                            {isHgSuperPay && selectedPayment && selectedPayment.sourceType && (
                                <SuperPayStatusPanel
                                    mygigstersStatus={selectedPayment.mygigstersStatus}
                                    sourceType={selectedPayment.sourceType}
                                    paymentStatus={selectedPayment.status}
                                    completedOn={selectedPayment.completedOn || selectedPayment.paymentDate}
                                    totalAmount={currencyHelper.formatMoney(displayTotal, 2)}
                                    reference={selectedPayment.reference || selectedPayment.externalReference}
                                    transactionId={selectedPayment.transactionId}
                                    currencySymbol={currencySymbol}
                                />
                            )}

                            {isHgSuperPay && selectedPayment && selectedPayment.failedLegs && selectedPayment.failedLegs.length > 0 && (
                                <div style={{ border: "1px solid #ffcdd2", borderRadius: 6, overflow: "hidden", marginBottom: 20 }}>
                                    <div style={{ background: "#C62828", padding: "10px 16px", display: "flex", alignItems: "center", gap: 8 }}>
                                        <span style={{ color: "#fff", fontSize: 16, lineHeight: 1 }}>⚠</span>
                                        <span style={{ color: "#fff", fontWeight: 700, fontSize: 14 }}>
                                            {selectedPayment.failedLegs.length === 1 ? "1 Failed Leg" : `${selectedPayment.failedLegs.length} Failed Legs`}
                                        </span>
                                        <span style={{ color: "rgba(255,255,255,0.75)", fontSize: 12, marginLeft: 4 }}>
                                            — these contributions could not be applied to the super fund
                                        </span>
                                    </div>
                                    <div style={{ background: "#fff8f8" }}>
                                        <Table style={{ width: "100%" }}>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell style={{ fontSize: 12, fontWeight: 700, color: "#666", padding: "8px 16px" }}>Artist</TableCell>
                                                    <TableCell style={{ fontSize: 12, fontWeight: 700, color: "#666", padding: "8px 16px" }}>Status</TableCell>
                                                    <TableCell style={{ fontSize: 12, fontWeight: 700, color: "#666", padding: "8px 16px" }}>Reason</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {selectedPayment.failedLegs.map((leg, idx) => (
                                                    <TableRow key={idx} style={{ borderTop: "1px solid #ffcdd2" }}>
                                                        <TableCell style={{ padding: "10px 16px", fontSize: 13, fontWeight: 600 }}>
                                                            {leg.payeeName || `Payee #${leg.payeeId}`}
                                                        </TableCell>
                                                        <TableCell style={{ padding: "10px 16px" }}>
                                                            <Chip
                                                                label={leg.status || "Failed"}
                                                                style={{ backgroundColor: "#FFEBEE", color: "#C62828", fontWeight: 700, fontSize: 11, height: 22 }}
                                                            />
                                                        </TableCell>
                                                        <TableCell style={{ padding: "10px 16px", fontSize: 13, color: "#555", lineHeight: 1.5 }}>
                                                            {leg.failureDetailedReason || leg.failureReason || "No reason provided"}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                        <div style={{ padding: "10px 16px", borderTop: "1px solid #ffcdd2", background: "#fff3f3", fontSize: 12, color: "#b71c1c" }}>
                                            To retry: update the affected artist's super fund details on their profile, then return to Super Ledgers and submit a new payment.
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                    {canModify && (
                                        <Checkbox
                                            checked={allSelected}
                                            indeterminate={selectedLedgerIds.size > 0 && selectedLedgerIds.size < filteredLedgers.length}
                                            onChange={this.handleSelectAll}
                                            style={{ padding: 4 }}
                                        />
                                    )}
                                    <Typography variant="subtitle1" style={{ fontWeight: 500 }}>
                                        Payees ({payeeIds.length}) - {filteredLedgers.length} Items
                                    </Typography>
                                </div>
                                {canModify && !isHgSuperPay && (
                                    <Button 
                                        onClick={this.handleRemoveFromPayment}
                                        color="secondary"
                                        variant="outlined"
                                        size="small"
                                        disabled={!hasSelectedItems || isRemoving}
                                    >
                                        {isRemoving ? "Removing..." : `Remove from Payment${hasSelectedItems ? ` (${selectedLedgerIds.size})` : ""}`}
                                    </Button>
                                )}
                            </div>

                            {payeeIds.length > 0 ? (
                                <div>
                                    {payeeIds.map(payeeId => {
                                        const payeeGroup = grouped[payeeId];
                                        const isExpanded = expandedPayees.has(payeeId);
                                        const isFullySelected = this.isPayeeFullySelected(payeeId);
                                        const isPartiallySelected = this.isPayeePartiallySelected(payeeId);
                                        const payeeDetail = isHgSuperPay ? this.getPayeePaymentDetail(payeeId) : null;
                                        const payeeReason = payeeDetail ? (payeeDetail.failureDetailedReason || payeeDetail.failureReason) : null;
                                        
                                        return (
                                            <ExpansionPanel 
                                                key={payeeId}
                                                expanded={isExpanded}
                                                onChange={() => this.handleExpandToggle(payeeId)}
                                                style={{ marginBottom: 8 }}
                                            >
                                                <ExpansionPanelSummary 
                                                    expandIcon={<ExpandMoreIcon />}
                                                    style={{ backgroundColor: "#f5f5f5" }}
                                                >
                                                    <div style={{ display: "flex", alignItems: "center", width: "100%", justifyContent: "space-between" }}>
                                                        <div style={{ display: "flex", alignItems: "center", flex: 1 }}>
                                                            {canModify && (
                                                                <Checkbox
                                                                    checked={isFullySelected}
                                                                    indeterminate={isPartiallySelected}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    onChange={(e) => {
                                                                        e.stopPropagation();
                                                                        this.handlePayeeToggle(payeeId);
                                                                    }}
                                                                    style={{ marginRight: 8 }}
                                                                />
                                                            )}
                                                            <div>
                                                                <div style={{ display: "flex", alignItems: "center" }}>
                                                                    <Typography style={{ fontWeight: 500 }}>
                                                                        {payeeGroup.payeeName}
                                                                    </Typography>
                                                                    <Chip 
                                                                        label={`${payeeGroup.items.length} item${payeeGroup.items.length !== 1 ? 's' : ''}`}
                                                                        size="small"
                                                                        style={{ marginLeft: 12, height: 20, fontSize: 11 }}
                                                                    />
                                                                    {payeeDetail && payeeDetail.status && (
                                                                        <span style={{ marginLeft: 10 }}>
                                                                            {this.getStatusChip(payeeDetail.status)}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                {payeeReason && !isExpanded && (
                                                                    <Typography variant="caption" style={{ color: "#795548", fontStyle: "italic", display: "block", marginTop: 2 }}>
                                                                        {payeeReason}
                                                                    </Typography>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <Typography style={{ fontWeight: 500, marginRight: 16 }}>
                                                            {this.getPayeeSubtotal(payeeGroup.items, currencySymbol)}
                                                        </Typography>
                                                    </div>
                                                </ExpansionPanelSummary>
                                                <ExpansionPanelDetails style={{ padding: 0 }}>
                                                    <Table size="small">
                                                        <TableHead>
                                                            <TableRow>
                                                                {canModify && <TableCell padding="checkbox" />}
                                                                <TableCell>Super Fee</TableCell>
                                                                <TableCell>Allocated Amount</TableCell>
                                                                <TableCell>Status</TableCell>
                                                                <TableCell>Booking</TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {payeeGroup.items.map((ledger, index) => (
                                                                <TableRow 
                                                                    key={ledger.id || index}
                                                                    selected={selectedLedgerIds.has(ledger.id)}
                                                                    hover
                                                                >
                                                                    {canModify && (
                                                                        <TableCell padding="checkbox">
                                                                            <Checkbox
                                                                                checked={selectedLedgerIds.has(ledger.id)}
                                                                                onChange={() => this.handleItemToggle(ledger.id)}
                                                                            />
                                                                        </TableCell>
                                                                    )}
                                                                    <TableCell>
                                                                        {currencySymbol}{currencyHelper.formatMoney(parseFloat(ledger.ledgerSuperFee || 0), 2)}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {currencySymbol}{currencyHelper.formatMoney(parseFloat(ledger.allocatedAmount || 0), 2)}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {this.getLedgerStatusLabel(ledger.ledgerStatus)}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {this.renderBookingLink(ledger.bookingId)}
                                                                    </TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                </ExpansionPanelDetails>
                                            </ExpansionPanel>
                                        );
                                    })}
                                </div>
                            ) : (
                                <Typography variant="body2" color="textSecondary" style={{ textAlign: "center", padding: 20 }}>
                                    No ledger items found for this payment.
                                </Typography>
                            )}
                        </Fragment>
                    )}
                </DialogContent>
                <DialogActions>
                    {viewMode !== 'payee' && (
                        <Button 
                            onClick={this.handleSyncPaymentStatus} 
                            color="secondary"
                            disabled={this.state.isSyncing}
                        >
                            {this.state.isSyncing
                                ? "Syncing..."
                                : paymentData.sourceType?.toLowerCase() === "mygigsters"
                                    ? "Sync HG Super Pay Status"
                                    : "Update Xero Pay Run Status"}
                        </Button>
                    )}
                    <Button onClick={this.handleClose} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
            </Fragment>
        );
    }
}

export default PaymentDetailModal;
